/**
 * Integration Models for Whitehelmet-NWC Unifier Integration
 * Following existing codebase patterns and naming conventions
 */

/**
 * Provider types for sync operations
 */
export type SyncProvider = 'projects' | 'p6' | 'observations';

export interface QueueTrackerData {
  id: number | null;
  completed: boolean;
  viewed: boolean;
  timestamp?: Date;
  results?: SyncResults;
  failed?: number;
  status?: 'completed' | 'failed' | 'processing' | 'queued';
  provider: SyncProvider;
}

export type QueueTracker = {
  [key in SyncProvider]: QueueTrackerData;
};

/**
 * Sync options for generic sync operations
 */
export interface SyncOptions {
  testMode: boolean;
  projectId?: string;
  customData?: any;
}

/**
 * Represents the result of a project sync operation
 */
export interface SyncResult {
  status: 'success' | 'error';
  data?: SyncResultData;
  meta_data?: SyncResultMetaData;
  message?: string;
  error_code?: string;
  errors?: ValidationError[];
}

export interface SyncResultProjectMetaData {
  id?: string;
  project_name: string;
  project_number: string;
}

/**
 * Data returned from successful sync operations
 */
export interface SyncResultData {
  // Set by the observations sync: the queue the caller can follow, and whether it was already
  // running (started by someone else or an earlier click) rather than newly queued.
  active_queue_id?: number | null;
  already_running?: boolean;
  projects_created: number;
  projects_updated: number;
  projects_matched: number;
  projects_failed: number;
  meta_data: {
    projects_created?: SyncResultProjectMetaData[];
    projects_updated?: SyncResultProjectMetaData[];
    projects_matched?: SyncResultProjectMetaData[];
    projects_failed?: SyncResultProjectMetaData[];
  };
  failures: SyncFailure[];
}

/**
 * Metadata for sync operations
 */
export interface SyncResultMetaData {
  timestamp: string;
  duration_ms: number;
  environment: 'uat' | 'production';
  user_id: string;
  current_adapter: 'nwc' | 'default';
}

/**
 * Represents a failed project sync
 */
export interface SyncFailure {
  project_number: string;
  project_name: string;
  reason: string;
}

/**
 * Validation error for sync operations
 */
export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Integration status for a project
 */
export interface IntegrationStatus {
  is_synced_from_unifier: boolean;
  unifier_ref_number?: string;
  unifier_updated_at?: string;
  sync_status: 'synced' | 'pending' | 'failed' | 'never_synced';
}

/**
 * Integration statistics for dashboard
 */
export interface IntegrationStatistics {
  total_projects: number;
  synced_projects: number;
  failed_projects: number;
  pending_projects: number;
  last_sync_timestamp?: string;
  last_sync_duration_ms?: number;
}

/**
 * Sync history record
 */
export interface SyncHistoryRecord {
  id: string;
  timestamp: string;
  type: 'project_sync' | 'manual_sync';
  status: 'success' | 'error';
  created: number;
  updated: number;
  failed: number;
  duration_ms: number;
  user_id: string;
  user_name: string;
  environment: 'uat' | 'production';
  adapter: 'nwc' | 'default';
}

/**
 * Integration configuration
 */
export interface IntegrationConfig {
  environment: 'uat' | 'production';
  current_adapter: 'nwc' | 'default';
  auto_sync_enabled: boolean;
  sync_interval_hours?: number;
  last_sync_timestamp?: string;
}

/**
 * Response for sync history API
 */
export interface SyncHistoryResponse {
  status: 'success' | 'error';
  data: SyncHistoryRecord[];
  meta_data: {
    current_page: number;
    per_page: number;
    total_pages: number;
    total_items: number;
  };
  message?: string;
}

/**
 * Integration dashboard data
 */
export interface IntegrationDashboardData {
  statistics: IntegrationStatistics;
  recent_syncs: SyncHistoryRecord[];
  configuration: IntegrationConfig;
  sync_failures: SyncFailure[];
}

/**
 * Connection test response from Unifier API
 */
export interface ConnectionTestResponse {
  status: 'success' | 'error';
  message: string;
  meta_data?: {
    timestamp: string;
    environment: string;
  };
  data: {
    status: 'success' | 'failed';
    message: string;
  };
  errors?: any[] | null;
}

// -----------------------------------------------------------------------------------------------------
// @ Queue Status Tracking Models (New)
// -----------------------------------------------------------------------------------------------------

/**
 * Represents progress information for a queue operation
 */
export interface QueueProgress {
  id: number;
  current_count: number;
  total_count: number;
  percentage: number;
  status: 'processing' | 'completed' | 'failed';
  message: string;
  arabic_message: string;
  created_at: string;
  purpose?: 'job' | 'logging';
  results?: SyncResults;
}

/**
 * Represents a queue status for sync operations
 */
export interface QueueStatus {
  id: number;
  provider: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  type: string;
  started_at: string;
  completed_at?: string;
  latest_progress: QueueProgress;
  progress: QueueProgress[];
  project_id: string;
  extra_data?: any;
}

/**
 * API response for queue statuses
 */
export interface QueueStatusResponse {
  status: 'success' | 'error';
  message: string;
  error_code: string | null;
  meta_data: any | null;
  data: QueueStatus[] | QueueStatus | null;
  errors: Array<{
    field: string;
    message: string;
  }> | null;
}

/**
 * Sync results from completed operations
 */

export type SyncResults = ProjectsSyncResult | P6SyncResult | ObservationsSyncResult;

/**
 * Progress bar state for UI components
 */
export interface ProgressBarState {
  isVisible: boolean;
  percentage: number;
  currentCount: number;
  totalCount: number;
  message: string;
  arabicMessage: string;
  status: 'processing' | 'completed' | 'failed';
  queueStatusId?: number;
}

/**
 * Sync results modal state
 */
export interface SyncResultsModalState {
  isVisible: boolean;
  results: SyncResults | null;
  testMode: boolean;
}

/**
 * Base sync result interface with common properties
 */
export interface BaseSyncResult {
  status: 'success' | 'error' | 'queued' | 'processing' | 'completed' | 'failed';
  timestamp: Date;
  provider: SyncProvider;
  failed: number;
  summary?: {
    test_mode?: boolean;
    project_name?: string;
    success_rate?: number;
    sync_timestamp?: Date;
    total_processed?: number;
  };
}

/**
 * Projects sync result type
 */
export interface ProjectsSyncResult extends BaseSyncResult {
  type: 'projects';

  projects_created: number;
  projects_updated: number;
  projects_matched: number;
  projects_failed: number;
  meta_data: {
    projects_created: SyncResultProjectMetaData[];
    projects_updated: SyncResultProjectMetaData[];
    projects_matched: SyncResultProjectMetaData[];
    projects_failed: SyncResultProjectMetaData[];
  };

  failures: SyncFailure[];
}

// {
//   "id": 41269,
//   "current_count": 3,
//   "total_count": 3,
//   "percentage": 100,
//   "status": "succeed",
//   "message": "Processed 3 of 3 reports (100%)",
//   "created_at": "2025-09-10 23:59:05",
//   "results": {
//       "failed_count": 0,
//       "created_count": 1,
//       "deleted_count": 0,
//       "matched_count": 0,
//       "updated_count": 0,
//       "failed_p6_reports": []
//   },
//   "failure_reason": [],
//   "metadata": {
//       "test_mode": true,
//       "chunk_progress": true
//   }
// }
/**
 * P6 sync result type
 */
export interface P6SyncResult extends BaseSyncResult {
  type: 'p6';
  created_count: number;
  updated_count: number;
  matched_count: number;
  failed_count: number;
  deleted_count: number;
  failed_p6_reports: Array<{
    report_name: string;
    reason: string;
    project_id?: string;
  }>;
}

/**
 * Observations sync result type
 */
export interface ObservationsSyncResult extends BaseSyncResult {
  type: 'observations';
  created_count: number;
  updated_count: number;
  matched_count: number;
  failed_count: number;
  aggregated_results: {
    created_count: number;
    updated_count: number;
    matched_count: number;
    failed_count: number;
    failed_observations:
      | {
          reason: string;
          message: string;
          rowData: {
            [key: string]: any;
          };
        }[][]
      | {
          reason: string;
          [key: string]: any;
        }[];
  };
  failed_observations:
    | {
        reason: string;
        message: string;
        rowData: {
          [key: string]: any;
        };
      }[]
    | {
        reason: string;
        [key: string]: any;
      }[];
}

/**
 * Union type for all sync results
 */
export type TypedSyncResult = ProjectsSyncResult | P6SyncResult | ObservationsSyncResult;

/**
 * Type guard functions for type-safe handling
 */
export function isProjectsSyncResult(result: TypedSyncResult): result is ProjectsSyncResult {
  return result.type === 'projects';
}

export function isP6SyncResult(result: TypedSyncResult): result is P6SyncResult {
  return result.type === 'p6';
}

export function isObservationsSyncResult(
  result: TypedSyncResult,
): result is ObservationsSyncResult {
  return result.type === 'observations';
}

/**
 * Get sync result type for runtime type checking
 */
export function getSyncResultType(result: TypedSyncResult): SyncProvider {
  return result.type;
}
